import {Button, Cascader, Checkbox, Divider, Empty, Form, Input, InputNumber, Popconfirm, Radio, RadioChangeEvent, Select, Space, Switch, Tabs, Tag, Tooltip} from "antd";
import {DefaultOptionType as CascaderOptionType} from "antd/lib/cascader";
import {FormikProps} from "formik";
import React, {FC, memo, useEffect, useRef, useState} from "react";
import {Link} from "react-router-dom";
import {InfoCircleTwoTone} from "@ant-design/icons";
import {blue, red} from "@ant-design/colors";
import {CheckboxChangeEvent} from "antd/lib/checkbox";
import moment from "moment";
import {URL_COURSES_TYPES} from "../../../models/url";
import {useCurrency} from "../../../helpers/currencyHelper";
import {
    CourseType,
    DiscountUnit,
    Instructor,
    PaymentInterval,
    useUpdateLocalLocationIdMutation,
    NewCourseLessonInput,
    EditCourseLessonInput,
    useSettingEndUserAppPublicQuery,
    MonthlySchedule,
    PaymentType,
    Maybe,
    Course,
} from "../../../generated/graphql";
import {createInputHelpers} from "../../../helpers/createInputHelpers";
import {getPopupContainer} from "../../../helpers/getPopupContainer";
import {isDefined} from "../../../helpers/typeScriptHelpers";
import i18n from "../../../services/i18n";
import {changeDefaultLessonCount} from "../courseHelpers";
import CustomInvoiceDueDaysInput from "./CustomInvoiceDueDaysInput";
import {PossibleFormikPropsType} from "../CourseCreate/CourseCreateForm";
import MoneyInput from "../../../views/MoneyInput/MoneyInput";
import InfoIconWithTooltip from "../../../views/InfoIconWithTooltip/InfoIconWithTooltip";
import {instructorLevelTag} from "../../../views/TableSelectInstructors/TableSelectInstructors";
import {margin, marginS} from "../../../styles/layout";
import IndividualDueDay from "./IndividualDueDay";
import css from "./CourseStepViews.less";
import cssMollie from "./MollieCoursePanel.less";
import EditRestrictiveBookingModal from "../../restrictiveBookings/CreateEdit/EditRestrictiveBookingModal";
import {defaultVatOptions, getLabel} from "../../settings/DefaultVat/DefaultVat";

/*
 *   ----------StepCourseData Components----------
 *
 *
 * CourseTypeInput Component
 */

type CourseTypeInputProps = {
    courseTypes: Array<Partial<CourseType>>;
    label: string;
    placeholder: string;
    addNewCourseTypeText: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    isLoading: boolean;
    testId: string;
    style?: React.CSSProperties;
    disabled?: boolean;
};

const CourseTypeInputComponent: FC<CourseTypeInputProps> = ({courseTypes, label, placeholder, addNewCourseTypeText, formikProps, isLoading, testId, style, disabled = false, ...props}) => {
    const {values, setValues, setFieldValue, initialValues} = formikProps;

    const {handleBlur, help, validateStatus} = createInputHelpers<PossibleFormikPropsType>(formikProps);

    const getValue = (key: keyof PossibleFormikPropsType, from: PossibleFormikPropsType = values) => {
        if (isDefined(from[key])) {
            return from[key];
        }

        return undefined;
    };

    const handleChange = (value: CourseType["id"]) => {
        const chosenCourseType = courseTypes.find(({id}) => id === value);

        if (chosenCourseType === undefined) {
            setFieldValue("courseTypeId", value);
        } else {
            setValues({
                ...values,
                courseTypeId: value,
                minAttendees: isDefined(chosenCourseType.minAttendees) ? chosenCourseType.minAttendees : initialValues.minAttendees,
                maxAttendees: isDefined(chosenCourseType.maxAttendees) ? chosenCourseType.maxAttendees : initialValues.maxAttendees,
                fee: isDefined(chosenCourseType.fee) ? chosenCourseType.fee : initialValues.fee,
                vat: isDefined(chosenCourseType.vat) ? chosenCourseType.vat : initialValues.vat,
                paymentInterval: isDefined(chosenCourseType.paymentInterval) ? chosenCourseType.paymentInterval : initialValues.paymentInterval,
                annotation: isDefined(chosenCourseType.annotation) ? chosenCourseType.annotation : initialValues.annotation,
                websiteInfo: isDefined(chosenCourseType.websiteInfo) ? chosenCourseType.websiteInfo : initialValues.websiteInfo,
                // Zusatzposten-Vorlagen der Kursart in den Kurs kopieren (ohne IDs) —
                // sie werden als eigene Kurs-Posten gespeichert und bleiben pro Kurs
                // entfern- und änderbar.
                // BEWUSST ohne id: Kursart-Posten sind Vorlagen — der Kurs bekommt
                // eigene, neue Zeilen (die Kursart-ID darf hier nie mitlaufen).
                additionalItems: (chosenCourseType.additionalItems ?? []).map((item) => ({
                    title: item.title,
                    grossPrice: item.grossPrice,
                    mode: item.mode,
                    vat: item.vat ?? null,
                    description: item.description ?? null,
                })),
            } as PossibleFormikPropsType);

            // FIX: Bei Kursart-Wechsel installmentRates NICHT auf [] setzen,
            // da ein leeres Array vom Backend als "alle Raten loeschen" interpretiert wird.
            // Stattdessen auf undefined setzen, um zu signalisieren "keine Aenderung".
            // Die urspruenglichen Raten bleiben erhalten.
            formikProps.setFieldValue("installmentRates", undefined);
        }
    };

    return (
        <div
            data-testid={testId}
            style={{
                gridColumn: "span 2",
                ...style,
            }}
            {...props}
        >
            <Form.Item
                label={label}
                validateStatus={validateStatus("courseTypeId")}
                // help={help("courseTypeId")}
                help={disabled ? "Nach dem Ändern der Kursart muss der Kurspreis angepasst werden" : help("courseTypeId")}
                required
            >
                <div id="courseDatesId">
                    <Select<CourseType["id"]>
                        // disabled={disabled}
                        placement="bottomLeft"
                        loading={isLoading}
                        placeholder={placeholder}
                        getPopupContainer={() => document.getElementById("courseDatesId") as HTMLElement}
                        dropdownRender={(menu) => (
                            <div style={{marginBottom: "4px"}}>
                                {menu}
                                <Divider style={{margin: "4px 0"}} />
                                <Link to={URL_COURSES_TYPES} style={{padding: "5px 12px"}} onMouseDown={(e) => e.preventDefault()}>
                                    {addNewCourseTypeText}
                                </Link>
                            </div>
                        )}
                        onChange={handleChange}
                        onBlur={handleBlur("courseTypeId")}
                        value={getValue("courseTypeId")}
                    >
                        {courseTypes.map((courseType) => (
                            <Select.Option key={courseType.id} value={courseType.id} label={courseType.name}>
                                <>
                                    <span
                                        style={{
                                            display: "inline-block",
                                            width: "0.9em",
                                            height: "0.9em",
                                            borderRadius: "50%",
                                            backgroundColor: courseType.color || blue[3],
                                            marginRight: 8,
                                            verticalAlign: "middle",
                                            flexShrink: 0,
                                        }}
                                    />
                                    {courseType.name}
                                    {courseType.showInWeb === false && <span style={{color: red[3]}}>{" im Web ausgeblendet!"}</span>}
                                </>
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </Form.Item>
        </div>
    );
};

export const CourseTypeInput = memo(CourseTypeInputComponent);

/*
 * CourseLocationInput Component
 */

type CourseLocationInputProps = {
    options: Array<CascaderOptionType>;
    label: string;
    placeholder: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    testId: string;
    value?: Array<string>;
    style?: React.CSSProperties;
    onlyEsrIsAvailable?: boolean;
    disabled?: boolean;
};

const CourseLocationInputComponent: FC<CourseLocationInputProps> = ({options, label, placeholder, formikProps, testId, style, value = [], onlyEsrIsAvailable, disabled = false, ...props}) => {
    const {values} = formikProps;
    const {help, validateStatus} = createInputHelpers<PossibleFormikPropsType>(formikProps);
    const [updateLocationId] = useUpdateLocalLocationIdMutation();

    return (
        <div data-testid={testId} style={{gridColumn: "1 / -1", ...style}} {...props}>
            <Form.Item label={label} validateStatus={validateStatus(["locationId", "venueId"])} help={help(["locationId", "venueId"])} required>
                <Cascader
                    getPopupContainer={getPopupContainer}
                    options={options}
                    value={value}
                    placeholder={placeholder}
                    disabled={disabled}
                    onChange={async (value) => {
                        // Cascader liefert [Stadt, Standort-ID, Ort-ID], die IDs sind Strings.
                        const [, locationId, venueId] = value as Array<string>;

                        formikProps.setValues({
                            ...values,
                            locationId,
                            venueId,
                            instructors: null,
                            lessons: ((values.lessons ?? []) as Array<NewCourseLessonInput | EditCourseLessonInput>).map((lesson) => ({
                                ...lesson,
                                locationId,
                                venueId,
                            })),
                        } as PossibleFormikPropsType);

                        await updateLocationId({
                            variables: {
                                id: isDefined(locationId) ? locationId : null,
                            },
                        });
                    }}
                />
            </Form.Item>
        </div>
    );
};

export const CourseLocationInput = memo(CourseLocationInputComponent);
/*
 *   ----------StepCourseSettings Components----------
 *
 *
 * CourseInstructorInput Component
 */

type CourseInstructorInputProps = {
    instructors: Array<Partial<Instructor>>;
    loading: boolean;
    label: string;
    placeholder: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    testId: string;
    style?: React.CSSProperties;
    disabled?: boolean;
};

const CourseInstructorInputComponent: FC<CourseInstructorInputProps> = ({instructors, loading, label, placeholder, formikProps, testId, style, disabled = false, ...props}) => {
    const {values, setValues, setFieldValue} = formikProps;

    const {help, validateStatus, handleBlur} = createInputHelpers<PossibleFormikPropsType>(formikProps);

    const instructorIds = values.instructors?.map(({id}) => id) ?? [];

    type LeaveDateResult = "leaveDateInPast" | "isLeaveDateInNext90Days" | null;

    const checkLeaveDate = (leaveDate: string | null | undefined): LeaveDateResult => {
        if (leaveDate === null || leaveDate === undefined) {
            return null;
        }
        const leaveDateInPast = new Date(leaveDate) < new Date();
        const leaveDateAsDate = new Date(leaveDate);
        const today = new Date();
        const ninetyDaysFromNow = new Date();

        ninetyDaysFromNow.setDate(today.getDate() + 90);

        const isLeaveDateInNext90Days = leaveDateAsDate < ninetyDaysFromNow;

        if (leaveDateInPast) {
            return "leaveDateInPast";
        }

        if (isLeaveDateInNext90Days) {
            return "isLeaveDateInNext90Days";
        }

        return null;
    };

    const renderLeaveDate = (leaveDate?: string | null, active?: boolean) => {
        if (leaveDate === null || leaveDate === undefined) {
            return "";
        } else if (active === false) {
            return "";
        } else if (leaveDate) {
            const leaveDateInPast = new Date(leaveDate) < new Date();
            const leaveDateAsDate = new Date(leaveDate);
            const today = new Date();
            const ninetyDaysFromNow = new Date();

            ninetyDaysFromNow.setDate(today.getDate() + 90);

            const isLeaveDateInNext90Days = leaveDateAsDate < ninetyDaysFromNow;

            if (leaveDateInPast) {
                return `Austrittsdatum liegt in der Vergangenheit ${moment(leaveDate).format("DD.MM.YYYY")}`;
            }

            if (isLeaveDateInNext90Days) {
                return `Austrittsdatum ist ${moment(leaveDate).format("DD.MM.YYYY")}`;
            }
        }

        return "";
    };

    const renderIsInactive = (active?: boolean) => {
        if (active) {
            return "";
        }

        return ` !${i18n.containers.courses.CourseCreate.courseTrainer.inactive()}`;
    };

    const instructorsOptions = instructors.map((instructor: Partial<Instructor>) => ({
        value: instructor.id,
        label: `${instructor.firstname} ${instructor.lastname}${instructorLevelTag(Number(instructor.level ?? 0))} ${renderLeaveDate(instructor.leaveDate, instructor.active)}${renderIsInactive(
            instructor.active,
        )}`,
        // disabled: instructor.active === false,
        level: instructor.level,
        className: instructor.active === false || checkLeaveDate(instructor.leaveDate) !== null ? css.inactive : "",
    }));

    return (
        <div
            data-testid={testId}
            style={{
                gridColumn: "span 1",
                ...style,
            }}
            {...props}
        >
            <Form.Item label={label} validateStatus={validateStatus("instructors")} help={help("instructors")} required>
                <Select
                    filterOption={(inputValue, option) => {
                        if (!option || !option.label) {
                            return false; // or true, depending on your logic
                        }

                        return option.label.toLowerCase().includes(inputValue.toLowerCase());
                    }}
                    filterSort={(optionA, optionB) => optionA.label.toLowerCase().localeCompare(optionB.label.toLowerCase())}
                    mode="multiple"
                    placeholder={placeholder}
                    loading={loading}
                    disabled={disabled}
                    notFoundContent={
                        loading ? (
                            "Loading ..."
                        ) : (
                            <Empty
                                className="ant-empty-small" // Not accessible through props, but the default empty style
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    instructors.length < 1
                                        ? i18n.containers.courses.CourseCreate.courseTrainer.emptyNoInstructors()
                                        : i18n.containers.courses.CourseCreate.courseTrainer.emptyForLocation()
                                }
                            />
                        )
                    }
                    onChange={(chosenInstructorIds: Array<Instructor["id"]>) => {
                        const preparedChosenInstructorIds = chosenInstructorIds.map((id) => ({id}));

                        if (Array.isArray(values.lessons)) {
                            const lessons = [...values.lessons];

                            lessons.forEach((lesson) => {
                                lesson.instructors = preparedChosenInstructorIds;
                            });

                            setValues({
                                ...values,
                                instructors: preparedChosenInstructorIds,
                                lessons,
                            } as PossibleFormikPropsType);
                        } else {
                            setFieldValue("instructors", preparedChosenInstructorIds);
                        }
                    }}
                    onBlur={handleBlur("instructors")}
                    getPopupContainer={getPopupContainer}
                    value={instructorIds}
                    options={instructorsOptions}
                />
            </Form.Item>
        </div>
    );
};

export const CourseInstructorInput = memo(CourseInstructorInputComponent);

/*
 * FeeInput Component
 */

type CourseFeeInputProps = {
    label: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    testId: string;
    placeholder?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
};

const CourseFeeInputComponent: FC<CourseFeeInputProps> = ({label, placeholder, formikProps, testId, style, disabled = false, ...props}) => {
    const {values} = formikProps;

    const {help, validateStatus, handleNumberInputChange, handleSelectInputChange, handleBlur} = createInputHelpers<PossibleFormikPropsType>(formikProps);

    const getValue = (key: keyof PossibleFormikPropsType, from: PossibleFormikPropsType = values) => {
        if (isDefined(from[key])) {
            return from[key];
        }

        return undefined;
    };

    return (
        <div data-testid={testId} style={{gridColumn: "span 1", ...style}} {...props}>
            <Form.Item label={label} validateStatus={validateStatus(["fee", "paymentInterval"])} help={help(["fee", "paymentInterval"])} required>
                <Input.Group compact>
                    <MoneyInput style={{width: "40%"}} onChange={handleNumberInputChange("fee")} onBlur={handleBlur("fee")} placeholder={placeholder} value={getValue("fee")} disabled={disabled} />
                    <Select<PaymentInterval>
                        style={{width: "60%"}}
                        onChange={handleSelectInputChange("paymentInterval")}
                        onBlur={handleBlur("paymentInterval")}
                        getPopupContainer={getPopupContainer}
                        value={getValue("paymentInterval")}
                        disabled={disabled}
                    >
                        <Select.Option key={PaymentInterval.PerCourse} value={PaymentInterval.PerCourse}>
                            {i18n.containers.courses.CourseCreate.paymentInterval.perCourse()}
                        </Select.Option>
                        <Select.Option key={PaymentInterval.PerLesson} value={PaymentInterval.PerLesson}>
                            {i18n.containers.courses.CourseCreate.paymentInterval.perLesson()}
                        </Select.Option>
                    </Select>
                </Input.Group>
            </Form.Item>
        </div>
    );
};

export const CourseFeeInput = memo(CourseFeeInputComponent);

type IncludedEntranceFeeInputProps = {
    label: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    testId: string;
    placeholder?: string;
    style?: React.CSSProperties;
};

// coursePrice steht nicht in PossibleFormikPropsType, das Feld liest und schreibt es trotzdem.
type EntranceFeeValues = {coursePrice?: Array<{includedEntranceFee?: number | null}>};
const entranceFeeField = "coursePrice[0]includedEntranceFee" as keyof PossibleFormikPropsType & string;

const IncludedEntranceFeeInputComponent: FC<IncludedEntranceFeeInputProps> = ({label, placeholder, formikProps, testId, style, ...props}) => {
    const coursePrice = (formikProps.values as EntranceFeeValues).coursePrice;
    const [entranceFee, setEntranceFee] = useState<{
        isIncluded: boolean;
        hasValue: boolean;
        value: number | null;
    }>({
        isIncluded: coursePrice?.[0].includedEntranceFee !== null && coursePrice?.[0].includedEntranceFee !== undefined,
        hasValue: coursePrice?.[0].includedEntranceFee !== null && coursePrice?.[0].includedEntranceFee !== 0,
        value: coursePrice?.[0].includedEntranceFee ?? null,
    });

    useEffect(() => {
        console.group("IncludedEntranceFeeInputComponent");
        console.log("includedEntranceFee", coursePrice?.[0].includedEntranceFee);
        console.log("isIncluded", entranceFee.isIncluded);
        console.log("hasValue", entranceFee.hasValue);
        console.log("value", entranceFee.value);
        console.groupEnd();
    }, [entranceFee, coursePrice]);

    const {values} = formikProps;

    // console.log("values", values);
    // console.log(
    //     "includedEntranceFee",
    //     values.coursePrice?.[0].includedEntranceFee,
    // );

    const {help, validateStatus, handleNumberInputChange, handleBlur} = createInputHelpers<PossibleFormikPropsType>(formikProps);

    const getValue = (from: PossibleFormikPropsType = values) => {
        const fee = (from as EntranceFeeValues).coursePrice?.[0].includedEntranceFee;

        if (isDefined(fee)) {
            return fee;
        }

        return undefined;
    };

    const handleIsIncluded = (checked: boolean) => {
        if (checked) {
            setEntranceFee({...entranceFee, isIncluded: true});
            formikProps.setFieldValue("coursePrice[0]includedEntranceFee", 0);
        } else {
            setEntranceFee({...entranceFee, isIncluded: false});
            formikProps.setFieldValue("coursePrice[0]includedEntranceFee", null);
        }
    };

    const handleEnterFee = (checked: boolean) => {
        // setEntranceFeeValue(0);
        if (checked) {
            setEntranceFee({...entranceFee, hasValue: true, value: 0});
            formikProps.setFieldValue("coursePrice[0]includedEntranceFee", 0);
        } else {
            setEntranceFee({...entranceFee, hasValue: false, value: 0});
            formikProps.setFieldValue("coursePrice[0]includedEntranceFee", 0);
        }
    };

    return (
        <div data-testid={testId} style={{...style}} {...props}>
            <Form.Item
                style={{
                    border: "1px solid lightGrey",
                    borderRadius: 5,
                    marginBottom: 10,
                    padding: "0 10px",
                    // marginTop: -10,
                }}
                label={
                    <Space size="small">
                        {i18n.containers.courses.CourseCreate.includedEntranceFee.label()}
                        <Tooltip title={i18n.containers.courses.CourseCreate.includedEntranceFee.popover.content()}>
                            <InfoCircleTwoTone twoToneColor={blue[5]} />
                        </Tooltip>
                    </Space>
                }
                validateStatus={validateStatus(entranceFeeField)}
                help={help(entranceFeeField)}
            >
                <div>
                    <Checkbox checked={entranceFee.isIncluded} onChange={(e: CheckboxChangeEvent) => handleIsIncluded(e.target.checked)}>
                        {i18n.containers.courses.CourseCreate.includedEntranceFee.feeNotIncluded()}
                    </Checkbox>
                    {entranceFee.isIncluded && (
                        <>
                            <Checkbox checked={entranceFee.hasValue} onChange={(e: CheckboxChangeEvent) => handleEnterFee(e.target.checked)}>
                                {i18n.containers.courses.CourseCreate.includedEntranceFee.enterFee()}
                            </Checkbox>

                            {entranceFee.hasValue && (
                                <MoneyInput
                                    style={{
                                        // width: "100%",
                                        marginBottom: 10,
                                        marginTop: 5,
                                    }}
                                    disabled={!entranceFee.hasValue}
                                    onChange={handleNumberInputChange(entranceFeeField)}
                                    onBlur={handleBlur(entranceFeeField)}
                                    placeholder={placeholder}
                                    value={getValue(values)}
                                />
                            )}
                        </>
                    )}
                </div>
            </Form.Item>
        </div>
    );
};

export const IncludedEntranceFeeInput = memo(IncludedEntranceFeeInputComponent);

/*
 * NumberInput component
 */

type NumberInputProps = {
    fieldName: keyof PossibleFormikPropsType;
    label: string;
    min?: number;
    max?: number;
    placeholder?: string;
    required?: boolean;
    formikProps: FormikProps<PossibleFormikPropsType>;
    testId: string;
    style?: React.CSSProperties;
};

const NumberInputComponent: FC<NumberInputProps> = ({fieldName, label, min, max, placeholder, required = true, formikProps, testId, style, ...props}) => {
    const {values} = formikProps;

    const {help, validateStatus, handleNumberInputChange, handleBlur} = createInputHelpers<PossibleFormikPropsType>(formikProps);

    const getValue = (key: keyof PossibleFormikPropsType, from: PossibleFormikPropsType = values) => {
        if (isDefined(from[key])) {
            return from[key];
        }

        return undefined;
    };

    return (
        <div data-testid={testId} style={{gridColumn: "span 1", ...style}} {...props}>
            <Form.Item label={label} validateStatus={validateStatus(fieldName)} help={help(fieldName)} required={required}>
                <InputNumber
                    min={min}
                    max={max}
                    placeholder={placeholder}
                    onChange={(value) => handleNumberInputChange(fieldName)(value as number | undefined)}
                    onBlur={handleBlur(fieldName)}
                    value={getValue(fieldName)}
                    style={{width: "100%"}}
                />
            </Form.Item>
        </div>
    );
};

export const NumberInput = memo(NumberInputComponent);

/*
 *   DiscountInput Component
 */

type CourseDiscountInputProps = {
    label: string;
    placeholder: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    style?: React.CSSProperties;
    testId: string;
};

const CourseDiscountInputComponent: FC<CourseDiscountInputProps> = ({label, placeholder, formikProps, testId, style, ...props}) => {
    // Der Enum-Wert heisst Euro, gemeint ist aber "fester Betrag" — angezeigt wird
    // deshalb das Währungszeichen des Mandanten, nicht fest ein Euro-Zeichen.
    const currency = useCurrency();
    const {values} = formikProps;
    const {discount, discountUnit} = values;

    const {help, validateStatus, handleNumberInputChange, handleSelectInputChange, handleBlur} = createInputHelpers<PossibleFormikPropsType>(formikProps);

    const getValue = (key: keyof PossibleFormikPropsType, from: PossibleFormikPropsType = values) => {
        if (isDefined(from[key])) {
            return from[key];
        }

        return undefined;
    };

    const enableSwitch = () => {
        const handleSwitch = (enabled: boolean) => {
            console.log("enabled", enabled);
            if (enabled) {
                formikProps.setFieldValue("discount", 10);
            } else {
                formikProps.setFieldValue("discount", 0);
            }

            console.log("discount::: ", values.discount);
        };

        return (
            <Switch
                size="small"
                style={{
                    marginTop: "auto",
                    marginBottom: 3,
                }}
                onChange={handleSwitch}
                checked={Boolean(isDefined(discount) ? discount > 0 : false)}
            />
        );
    };

    const renderLabel = () => {
        const InfoIconWithTooltipComponent = () => {
            return (
                <Tooltip title={<div style={{whiteSpace: "pre-line"}}>{i18n.containers.courses.CourseCreate.courseDiscount.description()}</div>}>
                    <InfoCircleTwoTone twoToneColor={blue[5]} style={{fontSize: 14, marginTop: 3}} />
                </Tooltip>
            );
        };

        return (
            <Space size={5} style={{paddingLeft: 2}}>
                {label}
                {enableSwitch()}
                <InfoIconWithTooltipComponent />
            </Space>
        );
    };

    return (
        <div
            data-testid={testId}
            style={{
                gridColumn: "span 1",
                display: "flex",
                ...style,
            }}
            {...props}
        >
            <Form.Item label={renderLabel()} validateStatus={validateStatus("discount")} help={help("discount")} style={{width: "100%"}}>
                <Input.Group compact>
                    <InputNumber
                        placeholder={placeholder}
                        onChange={(value) => handleNumberInputChange("discount")(value as number | undefined)}
                        onBlur={handleBlur("discount")}
                        style={{width: "50%"}}
                        value={getValue("discount")}
                        disabled={!discount || discount === 0}
                    />
                    <Select<string>
                        onChange={handleSelectInputChange("discountUnit")}
                        onBlur={handleBlur("discountUnit")}
                        style={{width: "50%"}}
                        getPopupContainer={getPopupContainer}
                        value={getValue("discountUnit") ?? DiscountUnit.Euro}
                        disabled={!discount || discount === 0}
                    >
                        <Select.Option key={DiscountUnit.Euro} value={DiscountUnit.Euro}>
                            {currency.symbol}
                        </Select.Option>
                        <Select.Option key={DiscountUnit.Percentage} value={DiscountUnit.Percentage}>
                            {i18n.containers.courses.CourseCreate.courseDiscount.units.percent()}
                        </Select.Option>
                    </Select>
                </Input.Group>
            </Form.Item>
        </div>
    );
};

export const CourseDiscountInput = memo(CourseDiscountInputComponent);

/*
 *   TaxInput Component
 */

type CourseTaxInputProps = {
    label: string;
    placeholder: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    style?: React.CSSProperties;
    testId: string;
};

const CourseTaxInputComponent: FC<CourseTaxInputProps> = ({label, placeholder, formikProps, testId, style, ...props}) => {
    const {values, setFieldValue} = formikProps;

    const {help, validateStatus, handleBlur} = createInputHelpers<PossibleFormikPropsType>(formikProps);

    const handleChange = (value: number | null) => {
        console.group("handleChange");
        console.log("value", value);
        console.groupEnd();
        if (value === null) {
            setFieldValue("vat", undefined);
        } else {
            setFieldValue("vat", value);
        }
    };

    return (
        <div data-testid={testId} style={{gridColumn: "span 1", ...style}} {...props}>
            <Form.Item label={i18n.containers.courses.CourseCreate.courseTax.label()} validateStatus={validateStatus("vat")} help={help("vat")} required>
                <InputNumber
                    onChange={handleChange}
                    onBlur={handleBlur("vat")}
                    value={isDefined(values.vat) ? values.vat : undefined}
                    style={{width: "100%"}}
                    min={0}
                    max={1}
                    step={0.01}
                    formatter={(value) => `${getLabel(value ?? 0, true)}`}
                    parser={(value) => Number(value?.replace("%", "")) / 100}
                />
            </Form.Item>
            <div style={{display: "flex", gap: "10px", marginTop: -15}}>
                {defaultVatOptions.map((option) => (
                    <Button
                        key={option.value}
                        onClick={() => handleChange(option.value)}
                        style={{
                            width: "100%",
                            borderColor: values.vat === option.value ? "unset" : blue[3],
                            color: values.vat === option.value ? "white" : blue[5],
                        }}
                        size="small"
                        block
                        type={values.vat === option.value ? "primary" : "default"}
                    >
                        {option.label}
                    </Button>
                ))}
            </div>
        </div>
    );
};

export const CourseTaxInput = memo(CourseTaxInputComponent);

/*
 *   TextAreaInput component
 */

type TextAreaInputProps = {
    fieldName: keyof PossibleFormikPropsType;
    rows: number;
    label: string;
    placeholder: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    style?: React.CSSProperties;
    testId: string;
};

export const TextAreaInput: FC<TextAreaInputProps> = ({fieldName, rows, label, formikProps, placeholder, testId, style, ...props}) => {
    const {values} = formikProps;

    const {help, validateStatus, handleTextInputChange, handleBlur} = createInputHelpers<PossibleFormikPropsType>(formikProps);

    // const getValue = (
    //     key: keyof PossibleFormikPropsType,
    //     from: PossibleFormikPropsType = values,
    // ) => {
    //     if (isDefined(from[key])) {
    //         return from[key];
    //     }

    //     return undefined;
    // };

    // this needs to be any because resizableTextArea is not in the typings
    const textAreaRef = useRef<any>(null);
    const handlePressEnter = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            event.persist();

            const currentValue = values[fieldName];
            const selectionStart = event.currentTarget.selectionStart;
            const selectionEnd = event.currentTarget.selectionEnd;

            // Insert a newline at the cursor's current position
            const newValue = currentValue.substring(0, selectionStart) + "\n" + currentValue.substring(selectionEnd);

            // Update the Formik field value
            formikProps.setFieldValue(fieldName, newValue, false);

            // Restore the cursor position after Formik state update
            setTimeout(() => {
                const textArea = textAreaRef.current?.resizableTextArea?.textArea; // Access the native textarea

                if (textArea) {
                    // Restore the cursor position
                    textArea.selectionStart = selectionStart + 1; // Move cursor after the new line
                    textArea.selectionEnd = selectionStart + 1; // Keep the cursor at the same position
                }
            }, 0);
        }
    };

    return (
        <div data-testid={testId} style={{gridColumn: "span 2", ...style}} {...props}>
            <Form.Item label={label} validateStatus={validateStatus(fieldName)} help={help(fieldName)}>
                <Input.TextArea
                    name={fieldName}
                    placeholder={placeholder}
                    autoSize={{minRows: rows, maxRows: 20}}
                    onChange={handleTextInputChange(fieldName)}
                    onBlur={handleBlur(fieldName)}
                    // value={getValue(fieldName)}
                    value={values[fieldName]}
                    onKeyDown={handlePressEnter} // Custom handling for Enter key
                    ref={textAreaRef} // Use ref for cursor control
                />
            </Form.Item>
        </div>
    );
};

/*
 *   ----------StepCourseLesson Components----------
 */

type CourseNumberOfLessonsInputProps = {
    label: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    style?: React.CSSProperties;
    testId: string;
};

const CourseNumberOfLessonsInputComponent: FC<CourseNumberOfLessonsInputProps> = ({label, formikProps, testId, style, ...props}) => {
    const {values} = formikProps;

    return (
        <div data-testid={testId} style={{gridColumn: "span 1", ...style}} {...props}>
            <Form.Item label={label} help="Maximal 53 Kurseinheiten erlaubt">
                <InputNumber
                    min={1}
                    max={53}
                    style={{width: "100%"}}
                    onChange={(value) => changeDefaultLessonCount(formikProps)(value as number | undefined)}
                    // value={values.defaultLessonCount ?? 0}
                    value={values.defaultLessonCount}
                />
            </Form.Item>
        </div>
    );
};

export const CourseNumberOfLessonsInput = memo(CourseNumberOfLessonsInputComponent);

type CoursePaymentCheckboxComponentProps = {
    label: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    defaultChecked?: boolean;
    style?: React.CSSProperties;
    testId: string;
    onlyEsrIsAvailable?: boolean;
    featureLessonBooking?: boolean;
};

const CoursePaymentCheckboxComponent: FC<CoursePaymentCheckboxComponentProps> = ({label, formikProps, testId, style, onlyEsrIsAvailable}) => {
    const {values} = formikProps;

    // Phase 3 (P3-2) — Mollie-Mode steuert das Verhalten der Bezahloptionen.
    // Online-fähige Kurse sind Vorkasse-only (Entscheidung A), Vorkasse trägt einen
    // modus-abhängigen Tag. Monatlich wird NICHT versteckt (Entscheidung B: sichtbare
    // Sperre), sondern sichtbar + disabled mit Tooltip, solange Online aktiv ist.
    // Individuelles Zahldatum bleibt voll bedienbar.
    const isMollieMode = values.allowMolliePayment === true;
    const isMollieOptional = values.mollieOptional === true;
    // Pflicht-Modus = Online aktiv UND nicht optional.
    const isMollieMandatory = isMollieMode && !isMollieOptional;
    // i18n IMMER als voller Pfad pro Aufruf (Proxy-Statefulness, siehe
    // ResetPaymentPlanModal.tsx:160) — KEINE Zwischenreferenz `const t = i18n.x.y`.

    const handleMonthlyScheduleChange = (e: RadioChangeEvent) => {
        return formikProps.setFieldValue("monthlySchedule", e.target.value as MonthlySchedule);
    };

    // Die Haken legen nur fest, was Kunden online wählen dürfen; das Büro bucht immer beide
    // Zahlweisen. Die Standard-Zahlweise ist für Kunden immer frei, die andere nur mit
    // „Kunden dürfen auch …“.
    const standard = values.paymentType === PaymentType.Monthly ? PaymentType.Monthly : PaymentType.PrePaid;
    const customersMayChoose = Boolean(values.allowUpFrontPayment && values.allowMonthlyPayment);
    const monthlyBlocked = isMollieMode || Boolean(onlyEsrIsAvailable);
    const monthlyBlockedTooltip = isMollieMode
        ? i18n.containers.courses.CourseCreate.molliePayment.onlineConflictTooltip()
        : onlyEsrIsAvailable
        ? `In den Einstellungen unter „Zahlungen“ ist als einzige Bezahlmethode "${i18n.views.PaymentMethodBadge.esr}" eingeschaltet. Daher ist diese Option nicht verfügbar.`
        : undefined;

    const setPaymentOptions = (paymentType: PaymentType, choice: boolean) => {
        const allowUpFrontPayment = paymentType === PaymentType.PrePaid || choice;

        formikProps.setFieldValue("paymentType", paymentType);
        formikProps.setFieldValue("allowUpFrontPayment", allowUpFrontPayment);
        formikProps.setFieldValue("allowMonthlyPayment", paymentType === PaymentType.Monthly || choice);
        if (!allowUpFrontPayment) {
            formikProps.setFieldValue("customInvoiceDueDays", null);
        }
    };

    return (
        <div style={{marginBottom: margin, gridColumn: "span 8", ...style}}>
            <Form.Item label={label} colon={false}>
                <Radio.Group name="paymentType" value={standard} onChange={(e: RadioChangeEvent) => setPaymentOptions(e.target.value as PaymentType, customersMayChoose)}>
                    <Radio value={PaymentType.PrePaid} disabled={isMollieMode}>
                        {i18n.containers.courses.CourseCreate.paymentOptions.options.allowPayUpFrontPayment.label()}
                        {isMollieMode && (
                            <Tag style={{marginLeft: 8, fontSize: 11}}>
                                <span className={cssMollie.mollieLogo} style={{padding: "1px 5px", fontSize: 9}}>{"mollie"}</span>
                                {" "}
                                {isMollieMandatory
                                    ? i18n.containers.courses.CourseCreate.molliePayment.mollieForcedTag()
                                    : "auch via mollie"}
                            </Tag>
                        )}
                    </Radio>
                    {/* Monatlich bleibt sichtbar und gesperrt (Entscheidung B: sichtbare Sperre). */}
                    <Tooltip title={monthlyBlockedTooltip}>
                        <Radio value={PaymentType.Monthly} disabled={monthlyBlocked}>
                            {i18n.containers.courses.CourseCreate.paymentOptions.options.allowMonthlyPayment.label()}
                        </Radio>
                    </Tooltip>
                </Radio.Group>
                <Tooltip title={monthlyBlockedTooltip}>
                    <Checkbox
                        style={{display: "flex", marginTop: marginS}}
                        name="customersMayChoosePaymentType"
                        checked={customersMayChoose}
                        disabled={monthlyBlocked}
                        onChange={(e) => setPaymentOptions(standard, e.target.checked)}
                    >
                        {standard === PaymentType.Monthly
                            ? i18n.containers.courses.CourseCreate.paymentOptions.customerChoice.upFront()
                            : i18n.containers.courses.CourseCreate.paymentOptions.customerChoice.monthly()}
                    </Checkbox>
                </Tooltip>
                {!monthlyBlocked && (
                    <div style={{fontSize: 12, color: "rgba(0,0,0,0.45)", marginTop: 4}}>
                        {i18n.containers.courses.CourseCreate.paymentOptions.officeHint()}
                    </div>
                )}
                {values.allowUpFrontPayment && (
                    <div style={{marginTop: marginS, marginBottom: 4}}>
                        <CustomInvoiceDueDaysInput formikProps={formikProps} />
                        {isMollieMode && (
                            <div style={{fontSize: 12, color: "rgba(0,0,0,0.45)", marginTop: 8, marginLeft: 24}}>
                                {"ℹ️ "}
                                {isMollieMandatory
                                    ? i18n.containers.courses.CourseCreate.molliePayment.mollieDueDateHint()
                                    : `${i18n.containers.courses.CourseCreate.molliePayment.mollieDueDateHint()} (für Kunden, die online zahlen)`}
                            </div>
                        )}
                    </div>
                )}
            </Form.Item>
            {!isMollieMode && values.allowMonthlyPayment && (
                <div>
                    <Radio.Group
                        style={{
                            marginLeft: margin,
                            marginTop: `calc(${marginS} * -1)`,
                        }}
                        value={values.monthlySchedule ?? MonthlySchedule.Consistent}
                        onChange={handleMonthlyScheduleChange}
                    >
                        <Radio value={MonthlySchedule.Consistent}>
                            <InfoIconWithTooltip
                                text={i18n.containers.courses.CourseCreate.monthlySchedule.consistent.label()}
                                tooltipTitle={i18n.containers.courses.CourseCreate.monthlySchedule.consistent.tooltipTitle()}
                                iconStyle={{
                                    alignSelf: "center",
                                }}
                            />
                        </Radio>
                        <Radio value={MonthlySchedule.Dynamic}>
                            <InfoIconWithTooltip
                                text={i18n.containers.courses.CourseCreate.monthlySchedule.dynamic.label()}
                                tooltipTitle={i18n.containers.courses.CourseCreate.monthlySchedule.dynamic.tooltipTitle()}
                                iconStyle={{
                                    alignSelf: "center",
                                }}
                            />
                        </Radio>
                        <Radio value={MonthlySchedule.Custom}>
                            <InfoIconWithTooltip
                                text={i18n.containers.courses.CourseCreate.monthlySchedule.custom.label()}
                                tooltipTitle={i18n.containers.courses.CourseCreate.monthlySchedule.custom.tooltipTitle()}
                                iconStyle={{
                                    alignSelf: "center",
                                }}
                            />
                        </Radio>
                    </Radio.Group>
                    {values.monthlySchedule !== "Custom" && (
                        <>
                            <Divider style={{marginTop: 10}} />
                            <IndividualDueDay formikProps={formikProps} />
                            <Divider />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export const CoursePaymentCheckbox = memo(CoursePaymentCheckboxComponent);

const CourseIsActiveCheckboxComponent: FC<CoursePaymentCheckboxComponentProps> = ({label, formikProps, testId, defaultChecked = true, style}) => {
    const {handleCheckboxChange} = createInputHelpers<PossibleFormikPropsType>(formikProps);
    const {values} = formikProps;

    return (
        <Form.Item style={{gridColumn: "span 2", ...style}} colon={false}>
            <Checkbox name="isActive" onChange={handleCheckboxChange("isActive")} defaultChecked={defaultChecked} checked={values.isActive ?? defaultChecked}>
                <Tooltip title="Kurse können inaktiv geschalten werden." placement="right">
                    {label}
                </Tooltip>
            </Checkbox>
        </Form.Item>
    );
};

export const CourseIsActiveCheckbox = memo(CourseIsActiveCheckboxComponent);

const CourseAllowLessonBookingCheckboxComponent: FC<CoursePaymentCheckboxComponentProps> = ({label, formikProps, testId, defaultChecked = false, style}) => {
    const {handleCheckboxChange} = createInputHelpers<PossibleFormikPropsType>(formikProps);
    const {values} = formikProps;

    return (
        <Form.Item style={{gridColumn: "span 2", ...style}} colon={false}>
            <Checkbox name="allowLessonBooking" onChange={handleCheckboxChange("allowLessonBooking")} defaultChecked={defaultChecked} checked={values.allowLessonBooking ?? defaultChecked}>
                <Tooltip title={i18n.containers.courses.Courses.allowLessonBooking.description()} placement="right">
                    {label}
                </Tooltip>
            </Checkbox>
        </Form.Item>
    );
};

export const CourseAllowLessonBookingCheckbox = memo(CourseAllowLessonBookingCheckboxComponent);

const OnlineBookingBlockedCheckboxComponent: FC<CoursePaymentCheckboxComponentProps> = ({label, formikProps, testId, defaultChecked = false, style}) => {
    const [popconfirmOpen, setPopconfirmOpen] = useState(false);
    const {values} = formikProps;

    const currentValue = values.blockBookingOnline ?? defaultChecked;

    const handleCheckboxChange = (e: React.MouseEvent) => {
        // Verhindere das native Checkbox-Verhalten komplett
        e.preventDefault();
        e.stopPropagation();

        console.log("handleCheckboxChange aufgerufen", {
            currentValue,
            valuesBlockBookingOnline: values.blockBookingOnline,
            defaultChecked,
            popconfirmOpen,
        });

        // Wenn momentan checked und soll unchecked werden -> direkt setzen
        if (currentValue) {
            formikProps.setFieldValue("blockBookingOnline", false);
        } else {
            // Wenn momentan unchecked und soll checked werden -> Popconfirm zeigen
            setPopconfirmOpen(true);
        }
    };

    const onConfirm = () => {
        formikProps.setFieldValue("blockBookingOnline", true);
        setPopconfirmOpen(false);
    };

    const onCancel = () => {
        setPopconfirmOpen(false);
    };

    return (
        <Form.Item style={{gridColumn: "span 2", ...style}} colon={false}>
            <Popconfirm
                open={popconfirmOpen}
                onConfirm={onConfirm}
                onCancel={onCancel}
                // title="Achtung: Wenn diese Option aktiviert ist, wird der Kurs online angezeigt, Teilnehmer können aber nicht buchen."
                title={
                    <div style={{textAlign: "center"}}>
                        <div
                            style={{
                                fontSize: "1.2em",
                                fontWeight: 600,
                                lineHeight: 1,
                            }}
                        >
                            {"Achtung"}
                        </div>
                        <br />
                        {"Wenn diese Option aktiviert ist, wird der Kurs online angezeigt,"}
                        <br />
                        {"Teilnehmer können aber nicht buchen."}
                    </div>
                }
                icon={null}
            >
                <Checkbox name="blockBookingOnline" onClick={handleCheckboxChange} checked={values.blockBookingOnline ?? defaultChecked}>
                    <Tooltip title="Wenn diese Option aktiviert ist, können Teilnehmer nicht online buchen." placement="right">
                        {label}
                    </Tooltip>
                </Checkbox>
            </Popconfirm>
        </Form.Item>
    );
};

export const OnlineBookingBlockedCheckbox = memo(OnlineBookingBlockedCheckboxComponent);

const CourseShowInWebCheckboxComponent: FC<CoursePaymentCheckboxComponentProps> = ({label, formikProps, testId, defaultChecked = true, style}) => {
    const [hideAfterDays, setHideAfterDays] = useState(28);

    // Bewusst die ungesperrte Public-Query: Diese Checkbox erscheint im
    // Kurs-Assistenten für alle Admins. Die gesperrte `settingEndUserApp`-Query
    // würde für Orgs ohne Web-Modul-Admin-Freigabe (Default) bei jedem
    // Kursanlegen einen ForbiddenError und damit eine "Aktion nicht möglich"-
    // Notification auslösen. Wir brauchen hier nur den Anzeigewert hideAfterDays.
    const {data} = useSettingEndUserAppPublicQuery();

    useEffect(() => {
        if (data) {
            setHideAfterDays(data.settingEndUserAppPublic.hideAfterDays);
        }
    }, [data]);

    const helperText = i18n.containers.courses.CourseCreate.helpBackToDays({
        hideAfterDays: hideAfterDays.toString(),
    });

    const {handleCheckboxChange} = createInputHelpers<PossibleFormikPropsType>(formikProps);
    const {values} = formikProps;

    return (
        <Form.Item style={{gridColumn: "span 2", ...style}} colon={false} help={helperText}>
            <Checkbox name="showInWeb" onChange={handleCheckboxChange("showInWeb")} checked={values.showInWeb ?? defaultChecked} defaultChecked={defaultChecked}>
                <Tooltip title="Kurse die auf Ihrer Internetseite angezeigt werden sollen." placement="right">
                    {label}
                </Tooltip>
            </Checkbox>
        </Form.Item>
    );
};

export const CourseShowInWebCheckbox = memo(CourseShowInWebCheckboxComponent);

type CourseNumberInputProps = {
    fieldName: keyof PossibleFormikPropsType;
    label: string;
    placeholder: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    style?: React.CSSProperties;
    testId: string;
    isActive: boolean;
};

export const CourseNumberInput: FC<CourseNumberInputProps> = ({fieldName, label, formikProps, placeholder, testId, style, isActive, ...props}) => {
    const {values} = formikProps;

    const {handleBlur, handleTextInputChange, help, validateStatus} = createInputHelpers<PossibleFormikPropsType>(formikProps);

    const getValue = (key: keyof PossibleFormikPropsType, from: PossibleFormikPropsType = values) => {
        if (isDefined(from[key])) {
            return from[key];
        }

        return undefined;
    };

    return (
        <div data-testid={testId} style={{gridColumn: "span 2", ...style}} {...props}>
            <Form.Item style={{marginTop: -5}} validateStatus={validateStatus(fieldName)} help={help(fieldName)}>
                <Input
                    placeholder={placeholder}
                    onChange={handleTextInputChange(fieldName)}
                    onBlur={handleBlur(fieldName)}
                    // defaultValue={getValue(fieldName) ?? "auto"}
                    value={getValue(fieldName)}
                    disabled={!isActive}
                    // maxLength={32}
                />
            </Form.Item>
        </div>
    );
};

type AdditionalShortInfoInputProps = {
    fieldName: keyof PossibleFormikPropsType;
    // rows: number;
    label: string;
    placeholder: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    style?: React.CSSProperties;
    testId: string;
};

export const AdditionalShortInfoInput: FC<AdditionalShortInfoInputProps> = ({fieldName, label, formikProps, placeholder, testId, style, ...props}) => {
    const {values} = formikProps;

    const {handleBlur, handleTextInputChange, help, validateStatus} = createInputHelpers<PossibleFormikPropsType>(formikProps);

    const getValue = (key: keyof PossibleFormikPropsType, from: PossibleFormikPropsType = values) => {
        if (isDefined(from[key])) {
            return from[key];
        }

        return undefined;
    };

    const Label = () => (
        <Space>
            {label}
            <Tooltip title={i18n.containers.courses.CourseCreate.additionalShortInfo.description()}>
                <InfoCircleTwoTone twoToneColor={blue[5]} />
            </Tooltip>
        </Space>
    );

    return (
        <div data-testid={testId} style={{gridColumn: "span 2", ...style}} {...props}>
            <Form.Item
                style={{marginTop: -5}}
                validateStatus={validateStatus(fieldName)}
                help={help(fieldName)}
                // label={label}
                label={<Label />}
            >
                <Input placeholder={placeholder} onChange={handleTextInputChange(fieldName)} onBlur={handleBlur(fieldName)} value={getValue(fieldName)} showCount />
            </Form.Item>
        </div>
    );
};

type CourseMaxWaitlistInputProps = {
    fieldName: keyof PossibleFormikPropsType;
    label: string;
    placeholder: string;
    formikProps: FormikProps<PossibleFormikPropsType>;
    style?: React.CSSProperties;
    testId: string;
    isActive: boolean;
};

const CourseMaxWaitlistInputComponent: FC<CourseMaxWaitlistInputProps> = ({fieldName, label, formikProps, placeholder, testId, style, isActive, ...props}) => {
    const [active, setActive] = useState<boolean>(isActive);
    const {values} = formikProps;
    const {handleBlur, handleTextInputChange, help, validateStatus} = createInputHelpers<PossibleFormikPropsType>(formikProps);

    const getValue = (key: keyof PossibleFormikPropsType, from: PossibleFormikPropsType = values) => {
        if (isDefined(from[key])) {
            return from[key];
        }

        return undefined;
    };

    const handleActiveChange = (checked: boolean) => {
        setActive(checked);
        if (checked) {
            formikProps.setFieldValue(fieldName, 0);
        } else {
            formikProps.setFieldValue(fieldName, null);
        }
    };

    const InfoIcon = () => {
        return (
            <Tooltip title={i18n.containers.courses.CourseCreate.maxWaitlist.description()} trigger="click">
                <InfoCircleTwoTone twoToneColor={blue[5]} />
            </Tooltip>
        );
    };

    const renderLabel = () => (
        <Space>
            <Switch size="small" onChange={handleActiveChange} checked={active} />
            {label}
            <InfoIcon />
        </Space>
    );

    const checkValueOnBlur = (value: Maybe<number> | undefined, fieldName: keyof PossibleFormikPropsType) => {
        if (value === undefined || value === null || !isDefined(value)) {
            formikProps.setFieldValue(fieldName, 0);
        }
        handleBlur(fieldName);
    };

    const currentValue = formikProps.values.maxWaitlist;

    return (
        <div data-testid={testId} style={{gridColumn: "span 2", ...style}} {...props}>
            <Form.Item validateStatus={validateStatus(fieldName)} help={currentValue === 0 ? "Es können sich keine Teilnehmer auf die Warteliste einbuchen!" : help(fieldName)} label={renderLabel()}>
                <Input
                    placeholder={placeholder}
                    onChange={handleTextInputChange(fieldName)}
                    onBlur={() => checkValueOnBlur(currentValue, fieldName)}
                    value={getValue(fieldName)}
                    disabled={!isActive}
                />
            </Form.Item>
        </div>
    );
};

export const CourseMaxWaitlistInput = memo(CourseMaxWaitlistInputComponent);

type EditRestrictiveBookingInputProps = {
    formikProps: FormikProps<PossibleFormikPropsType>;
    prefixedCourseNumber?: string;
    testId: string;
    courseId: Maybe<Course["id"]> | undefined;
    style?: React.CSSProperties;
};

const EditRestrictiveBookingInputComponent: FC<EditRestrictiveBookingInputProps> = ({formikProps, prefixedCourseNumber, testId, courseId, style}) => {
    const disabled = !courseId;
    // const prefixedCourseNumber = formikProps.values.prefixedCourseNumber;

    const toolTipText = disabled ? i18n.containers.courses.CourseCreate.restrictiveBooking.tooltipText() : i18n.containers.courses.CourseCreate.restrictiveBooking.description();

    const Label = () => (
        <Space>
            {i18n.containers.courses.CourseCreate.restrictiveBooking.label()}
            <Tooltip title={toolTipText}>
                <InfoCircleTwoTone twoToneColor={blue[5]} />
            </Tooltip>
        </Space>
    );

    return (
        <div
            data-testid={testId}
            style={{
                gridColumn: "span 2",
                ...style,
            }}
        >
            <Form.Item label={<Label />} colon={false} style={{marginTop: -5}}>
                <EditRestrictiveBookingModal
                    courseId={courseId}
                    prefixedCourseNumber={prefixedCourseNumber}
                    tooltipText={disabled ? i18n.containers.courses.CourseCreate.restrictiveBooking.tooltipText() : undefined}
                />
            </Form.Item>
        </div>
    );
};

export const EditRestrictiveBookingInput = memo(EditRestrictiveBookingInputComponent);
